/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Canceler } from "axios";
//context
import { useOrdersValue } from "../context";
//custom libraries
import { useOrderHandler, useCustomerHandler } from "./rest";
import { useShopOnBehalf } from "./use-shop-on-behalf";
import { PATH_CART } from "../constants/order";
import { ORDER } from "../constants/routes";
import { useOrgResource } from "./use-org-resource";
import { useSelectedStoreValue } from "../context";
import { useCustomer } from "./use-customer";
import { LIMIT, ZERO_OFFSET } from "../constants/common";

export const useOrderDetails = () => {
  const cancels: any = { cancels: [] };
  const { getCustomerByIdRequest } = useCustomerHandler();
  const { shopOnBehalf } = useShopOnBehalf();
  const history = useHistory();
  const { orderId } = useParams<{ orderId: string }>();
  const { orders, setOrders } = useOrdersValue();
  const {
    lockOrderREST,
    unlockOrderREST,
    cancelOrder,
    findOrderByID,
    postOrderCommentByID,
    getOrderCommentByID,
    reOrder,
  } = useOrderHandler();
  const cancelers: any = { cancels: [] };
  const [pageNumber, setPageNumber] = useState(1);
  const [orderDetail, setOrderDetail] = useState<any>({});
  const [orderItem, setOrderItem] = useState([]);
  const [inputComment, setInputComment] = useState("");
  const [orderComment, setOrderComment] = useState<any>([]);
  const {
    toggleCustomerStatus,
    fetchCustomer,
    cusinfo,
    setCusinfo,
    handleChange,
    updateCustomer,
    canSave,
    editFormData,
    initializeEditForm,
    displayAlert,
    setDisplayAlert,
  } = useCustomer();

  const { findOrders } = useOrderHandler();
  const { selectedStore } = useSelectedStoreValue();
  const { orgInfo, setOrgInfo, getOrgById } = useOrgResource();

  const getPrevious = () => {
    setPageNumber(pageNumber - 1);
    if (pageNumber === 1) {
      getOrderByID();
    }
  };

  const getNext = () => {
    setPageNumber(pageNumber + 1);
  };

  const updateOrderSearchResult = () => {
    const [res, canceler] = findOrders({ orderId }, ZERO_OFFSET, LIMIT);
    cancels.cancels = cancels.cancels.concat(canceler);

    res.then((r: any) => {
      if (orders.items) {
        const newOrderItems = orders.items.map((o: any) => {
          if (o.orderId === r.data.items[0].orderId) {
            return Object.assign({ ...o }, r.data.items[0]);
          } else {
            return { ...o };
          }
        });

        setOrders({ ...orders, items: newOrderItems });
      }
    });
  };

  const getOrderByID = () => {
    const [res, canceler] = findOrderByID(orderId, pageNumber);
    cancelers.cancels = canceler;
    return res.then((r: any) => {
      setOrderDetail(r.data);
      setOrderItem(r.data?.orderItem);
      r.data.buyerId && fetchCustomer(r.data.buyerId);
      r.data.orgUniqueID &&
        getOrgById(r.data.orgUniqueID).then((r) => {
          setOrgInfo(r.address);
        });
    });
  };

  const cancelOrderByID = () => {
    const [res, canceler] = cancelOrder(orderId);
    cancelers.cancels = canceler;
    res.then((r: any) => {
      getOrderByID();
      updateOrderSearchResult();
    });
  };

  const lockOrder = (takeOver: boolean, oid?: string) => {
    let _oid = oid;
    if (!_oid) _oid = orderId;
    const [res, canceler] = lockOrderREST(_oid, takeOver);
    cancels.cancels = canceler;
    if (oid) {
      //get order by id for reorder lock and continue
      return res;
    } else {
      res.then((r: any) => {
        getOrderByID();
        updateOrderSearchResult();
      });
    }
  };

  const unlockOrder = () => {
    const [res, canceler] = unlockOrderREST(orderId);
    cancels.cancels = canceler;
    res.then((r: any) => {
      getOrderByID();
      updateOrderSearchResult();
    });
  };

  const handleLockUnLock = () => {
    if (orderDetail.isLocked !== undefined) {
      orderDetail.isLocked
        ? lockOrder(true)
        : orderDetail.checkLock
        ? unlockOrder()
        : lockOrder(false);
    }
  };

  const checkout = () => {
    const [res, canceler] = getCustomerByIdRequest(orderDetail.buyerId);
    cancels.cancels = canceler;
    res.then((r) => {
      shopOnBehalf(r.data, PATH_CART);
    });
  };

  const postCommentByID = () => {
    const [res, canceler] = postOrderCommentByID(orderId, inputComment.trim());
    cancelers.cancels = canceler;
    return res.then((r: any) => {
      setInputComment("");
      getCommentByID();
    });
  };

  const getCommentByID = () => {
    console.log("get copmment");
    const [res, canceler] = getOrderCommentByID(orderId);
    cancelers.cancels = canceler;
    res.then((r: any) => {
      setOrderComment(r.data?.orderComments);
    });
  };

  const orderHandleChange = (event: any) => {
    setInputComment(event.target.value);
  };

  const cancelChange = () => {
    setInputComment("");
  };

  const saveChange = () => {
    return postCommentByID();
  };

  const reOrderByOrderId = (skipMessageSnackbar = false) => {
    const params = {
      fromOrderId_1: orderId,
      forUserId: orderDetail.buyerId,
      toOrderId: ".**.",
      copyOrderItemId_1: "*",
      inventoryValidation: true,
    };
    const [res, canceler] = reOrder(params, skipMessageSnackbar);
    cancelers.cancels = canceler;
    return res.then((r: any) => {
      const newOrderId = r.data?.orderId[0];
      const location = {
        pathname: `${ORDER}/${newOrderId}`,
        state: {
          from: ORDER,
          label: "Common.BackToOrderSearchResults",
        },
      };
      history.replace(location);
    });
  };

  const updateOrderSearchResultBuyer = (buyer: any) => {
    if (orders.items) {
      const items = [...orders.items];
      items
        .filter((o: any) => {
          return o.memberId === buyer.id;
        })
        .forEach((o: any) => {
          o.buyer = buyer;
        });
      setOrders({ ...orders, items });
    }
  };

  const clickSubmit = (event: any) => {
    event.preventDefault();
    updateCustomer()
      .then((r: any) => {
        setDisplayAlert(true);
        setTimeout(() => setDisplayAlert(false), 4000);
        return (
          cusinfo?.id &&
          fetchCustomer(cusinfo?.id).then((buyer) =>
            updateOrderSearchResultBuyer(buyer)
          )
        );
      })
      .catch((e: any) => console.log(e));
  };

  const changeCustomerStatus = (customer: any) => {
    toggleCustomerStatus(customer)
      .then((r: any) => {
        fetchCustomer(customer.id).then((buyer) =>
          updateOrderSearchResultBuyer(buyer)
        );
      })
      .catch((e: any) => console.log(e));
  };

  useEffect(() => {
    getOrderByID().finally(() => {
      getCommentByID();
    });
    return () => {
      cancelers.canceler &&
        cancelers.canceler.forEach((cancel: Canceler) => cancel());
    };
  }, [orderId]);

  useEffect(() => {
    if (pageNumber > 1) {
      getOrderByID();
    }
  }, [pageNumber]);

  return {
    getPrevious,
    getNext,
    getOrderByID,
    orderDetail,
    orderItem,
    handleLockUnLock,
    cancelOrderByID,
    checkout,
    getCommentByID,
    orderComment,
    inputComment,
    saveChange,
    orderHandleChange,
    cancelChange,
    reOrderByOrderId,
    lockOrder,
    setCusinfo,
    selectedStore,
    orgInfo,
    cusinfo,
    handleChange,
    clickSubmit,
    changeCustomerStatus,
    canSave,
    editFormData,
    initializeEditForm,
    displayAlert,
  };
};
